// Regras puras da condição de pagamento do fornecedor e das parcelas do pedido
// de compra geradas a partir dela. Datas são texto ISO (AAAA-MM-DD) do começo
// ao fim — nada passa por fuso horário.

import type {
  DueDateBase,
  ProviderPaymentTerm,
  ProviderPaymentTermInstallment,
} from '../../types/providerPaymentTerm';
import { DUE_DATE_BASE_DELIVERY, DUE_DATE_BASE_ORDER } from '../../types/providerPaymentTerm';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Soma dias a uma data ISO ("2026-12-20" + 15 → "2027-01-04"). Inválida → ''. */
export function addDaysISO(iso: string | undefined, days: number): string {
  const m = ISO_DATE.exec((iso ?? '').slice(0, 10));
  if (!m) return '';
  // Date.UTC só como calendário: entra e sai em UTC, então não há fuso no meio.
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Soma meses a uma data ISO, segurando no último dia do mês (31/01 + 1 → 28/02). */
export function addMonthsISO(iso: string | undefined, months: number): string {
  const m = ISO_DATE.exec((iso ?? '').slice(0, 10));
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1 + months;
  const lastDay = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
  const d = new Date(Date.UTC(y, mo, Math.min(Number(m[3]), lastDay)));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Percentuais iguais com 4 casas; a última parcela fica com o resto (soma = 100). */
export function splitPercents(count: number): number[] {
  if (count <= 0) return [];
  const base = Math.floor((100 / count) * 10000) / 10000;
  const out = Array.from({ length: count }, () => base);
  out[count - 1] = Math.round((100 - base * (count - 1)) * 10000) / 10000;
  return out;
}

/** "30/60/90", "30, 45" ou "30 60" → [30, 60, 90]. Vazio ou inválido → null. */
export function parseDaysList(text: string): number[] | null {
  const parts = text.split(/[\s/,;]+/).filter(Boolean);
  if (parts.length === 0) return null;
  const days = parts.map((p) => (/^\d{1,4}$/.test(p) ? Number(p) : NaN));
  return days.some((d) => Number.isNaN(d)) ? null : days;
}

/** Parcelas da condição a partir de uma lista de dias, todas na mesma base. */
export function installmentsFromDays(days: number[], base: DueDateBase): ProviderPaymentTermInstallment[] {
  const pcts = splitPercents(days.length);
  return days.map((d, i) => ({ seq: i + 1, due_date_base: base, days: d, percent: pcts[i] }));
}

/** Soma dos percentuais arredondada a 4 casas. */
export const percentTotal = (installments: Pick<ProviderPaymentTermInstallment, 'percent'>[]): number =>
  Math.round(installments.reduce((s, i) => s + (Number(i.percent) || 0), 0) * 10000) / 10000;

/** Mensagem de erro da condição antes de salvar (mesmas regras do backend), ou null. */
export function validateTerm(term: { payment_method_id?: number; installments: ProviderPaymentTermInstallment[] }): string | null {
  if (!term.payment_method_id) return 'Informe a forma de pagamento.';
  if (term.installments.length === 0) return 'Informe ao menos uma parcela.';
  if (term.installments.length > 24) return 'No máximo 24 parcelas.';
  for (let i = 0; i < term.installments.length; i++) {
    const inst = term.installments[i];
    if (!Number.isInteger(inst.days) || inst.days < 0 || inst.days > 3650) return `Parcela ${i + 1}: dias entre 0 e 3650.`;
    if (!(inst.percent > 0)) return `Parcela ${i + 1}: percentual deve ser maior que zero.`;
  }
  const total = percentTotal(term.installments);
  if (Math.abs(total - 100) > 0.01) {
    return `A soma dos percentuais deve ser 100% (está em ${total.toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%).`;
  }
  return null;
}

/** Rótulo curto da base de uma parcela: "na emissão", "30d após a entrega". */
export function dueBaseLabel(base: DueDateBase | null | undefined, days: number | null | undefined): string {
  if (base !== DUE_DATE_BASE_ORDER && base !== DUE_DATE_BASE_DELIVERY) return 'data fixa';
  const what = base === DUE_DATE_BASE_ORDER ? 'emissão' : 'entrega';
  return !days ? `na ${what}` : `${days}d após a ${what}`;
}

const fmtPct = (p: number) => `${p.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/**
 * Resumo legível: "30/45 dias após a entrega" quando todas as parcelas têm a
 * mesma base e o mesmo percentual; senão, parcela a parcela
 * ("50% na emissão + 50% na entrega").
 */
export function describeTerm(installments: ProviderPaymentTermInstallment[]): string {
  if (installments.length === 0) return '';
  const sameBase = installments.every((i) => i.due_date_base === installments[0].due_date_base);
  const pcts = splitPercents(installments.length);
  const equal = installments.every((i, idx) => Math.abs(i.percent - pcts[idx]) < 0.01);
  if (sameBase && equal && installments.some((i) => i.days > 0)) {
    const what = installments[0].due_date_base === DUE_DATE_BASE_ORDER ? 'emissão' : 'entrega';
    return `${installments.map((i) => i.days).join('/')} dias após a ${what}`;
  }
  return installments.map((i) => `${fmtPct(i.percent)} ${dueBaseLabel(i.due_date_base, i.days)}`).join(' + ');
}

/** Parcela do pedido de compra gerada (formato do formulário, sem _tempId). */
export interface GeneratedInstallment {
  value: number;
  due_date: string;
  notes: string;
  payment_method_id?: number;
  due_date_base: DueDateBase;
  due_days: number;
}

export interface BuildResult {
  installments: GeneratedInstallment[];
  /** Há parcela na emissão e a Data do Pedido está vazia. */
  missingOrderDate: boolean;
  /** Há parcela na entrega e a Data Prevista está vazia. */
  missingExpectedDate: boolean;
}

/**
 * Parcelas do pedido a partir da condição do fornecedor. Valor = total × % em
 * centavos (truncado); a última parcela fica com o resto, para a soma fechar.
 * Sem a data-base de alguma parcela, não gera nada e diz qual data falta.
 */
export function buildInstallmentsFromTerm(
  term: Pick<ProviderPaymentTerm, 'installments' | 'payment_method_id'>,
  total: number,
  orderDate: string | undefined,
  expectedDate: string | undefined,
): BuildResult {
  const needsOrder = term.installments.some((i) => i.due_date_base === DUE_DATE_BASE_ORDER);
  const needsDelivery = term.installments.some((i) => i.due_date_base === DUE_DATE_BASE_DELIVERY);
  const missingOrderDate = needsOrder && !addDaysISO(orderDate, 0);
  const missingExpectedDate = needsDelivery && !addDaysISO(expectedDate, 0);
  if (missingOrderDate || missingExpectedDate || term.installments.length === 0) {
    return { installments: [], missingOrderDate, missingExpectedDate };
  }
  const totalCents = Math.round(total * 100);
  const n = term.installments.length;
  let used = 0;
  const installments = term.installments.map((inst, idx) => {
    const cents = idx === n - 1 ? totalCents - used : Math.floor((totalCents * inst.percent) / 100);
    used += cents;
    const base = inst.due_date_base === DUE_DATE_BASE_ORDER ? orderDate : expectedDate;
    return {
      value: cents / 100,
      due_date: addDaysISO(base, inst.days),
      notes: `${idx + 1}ª parcela (${dueBaseLabel(inst.due_date_base, inst.days)})`,
      payment_method_id: term.payment_method_id,
      due_date_base: inst.due_date_base,
      due_days: inst.days,
    };
  });
  return { installments, missingOrderDate: false, missingExpectedDate: false };
}

/**
 * Parcelas mensais (sem condição do fornecedor): 1, 2, 3… meses depois da data
 * do pedido; o resto dos centavos vai para a primeira. Data fixa (sem base).
 */
export function buildMonthlyInstallments(
  count: number,
  total: number,
  orderDate: string,
  paymentMethodId?: number,
): { value: number; due_date: string; notes: string; payment_method_id?: number }[] {
  if (count <= 0) return [];
  const totalCents = Math.round(total * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;
  return Array.from({ length: count }, (_, idx) => ({
    value: (idx === 0 ? base + remainder : base) / 100,
    due_date: addMonthsISO(orderDate, idx + 1),
    notes: `${idx + 1}ª parcela`,
    payment_method_id: paymentMethodId,
  }));
}

export interface DueDateChange {
  /** Número da parcela (1-based, na ordem da tabela). */
  n: number;
  before: string;
  after: string;
}

interface RecalcLike {
  due_date: string;
  due_date_base?: number | null;
  due_days?: number | null;
}

/**
 * Recalcula o vencimento das parcelas que têm base (emissão/entrega + dias) a
 * partir das datas atuais do pedido. Parcelas com data fixa (legado ou
 * editadas à mão) não mudam; data-base vazia também não mexe na parcela.
 */
export function recalcDueDates<T extends RecalcLike>(
  installments: T[],
  orderDate: string | undefined,
  expectedDate: string | undefined,
): { installments: T[]; changes: DueDateChange[] } {
  const changes: DueDateChange[] = [];
  const next = installments.map((inst, idx) => {
    if (inst.due_date_base == null || inst.due_days == null) return inst;
    const base = inst.due_date_base === DUE_DATE_BASE_ORDER ? orderDate : inst.due_date_base === DUE_DATE_BASE_DELIVERY ? expectedDate : '';
    const after = addDaysISO(base, inst.due_days);
    if (!after || after === inst.due_date) return inst;
    changes.push({ n: idx + 1, before: inst.due_date, after });
    return { ...inst, due_date: after };
  });
  return { installments: changes.length ? next : installments, changes };
}

/** Forma de pagamento de cartão de crédito ("Cartão de crédito a vista/parcelado"). */
export const isCreditCardMethodName = (name: string | undefined): boolean =>
  /cart[aã]o\s+de\s+cr[eé]dito/i.test(name ?? '');

/** Cartão sugerido: o único cartão disponível, senão nenhum (a pessoa escolhe). */
export const defaultCardId = (cards: { credit_card_id: number }[]): number | undefined =>
  cards.length === 1 ? cards[0].credit_card_id : undefined;

/**
 * Erros das parcelas pagas com cartão sem cartão escolhido (chave
 * `installments.N.credit_card_id`). Sem cartão cadastrado na empresa não há o
 * que escolher — a parcela provisiona na conta, como antes.
 */
export function cardInstallmentErrors(
  installments: { payment_method_id?: number; credit_card_id?: number | null }[],
  isCardMethod: (paymentMethodId: number | undefined) => boolean,
  cardsAvailable: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!cardsAvailable) return errors;
  installments.forEach((inst, idx) => {
    if (isCardMethod(inst.payment_method_id) && !inst.credit_card_id) {
      errors[`installments.${idx}.credit_card_id`] = `Parcela ${idx + 1}: escolha o cartão`;
    }
  });
  return errors;
}
